import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Badge, Modal,
  NumberInput, Select, Textarea, Stack, Loader, Center,
  ActionIcon, Tooltip
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconPencil, IconTrash, IconCoin } from '@tabler/icons-react';
import 'dayjs/locale/it';

function CommissioniAllRent() {
  const [commissioni, setCommissioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommissione, setEditingCommissione] = useState(null);
  const [form, setForm] = useState({
    mese_riferimento: null, importo_previsto: 0, sede: null, note: ''
  });
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => { loadCommissioni(); }, []);

  const loadCommissioni = async () => {
    try {
      const res = await api.get('/commissioni-allrent');
      setCommissioni(res.data);
    } catch (err) {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare le commissioni', color: 'red' });
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const meseRif = form.mese_riferimento ?
        `${form.mese_riferimento.getFullYear()}-${String(form.mese_riferimento.getMonth() + 1).padStart(2, '0')}-01` : null;
      const payload = {
        mese_riferimento: meseRif,
        importo_previsto: form.importo_previsto || 0,
        sede: form.sede || null,
        note: form.note || null,
      };
      if (editingCommissione) {
        await api.put(`/commissioni-allrent/${editingCommissione.id}`, payload);
        notifications.show({ title: 'Successo', message: 'Commissione aggiornata', color: 'green' });
      } else {
        await api.post('/commissioni-allrent', payload);
        notifications.show({ title: 'Successo', message: 'Commissione creata', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadCommissioni();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  const handleEdit = (commissione) => {
    setEditingCommissione(commissione);
    setForm({
      mese_riferimento: commissione.mese_riferimento ? new Date(commissione.mese_riferimento) : null,
      importo_previsto: commissione.importo_previsto || 0,
      sede: commissione.sede || null,
      note: commissione.note || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (commissione) => {
    const meseLabel = formatMese(commissione.mese_riferimento);
    if (window.confirm(`Eliminare la commissione di ${meseLabel}?`)) {
      try {
        await api.delete(`/commissioni-allrent/${commissione.id}`);
        notifications.show({ title: 'Successo', message: 'Commissione eliminata', color: 'green' });
        loadCommissioni();
      } catch (err) {
        notifications.show({ title: 'Errore', message: "Errore nell'eliminazione", color: 'red' });
      }
    }
  };

  const resetForm = () => {
    setEditingCommissione(null);
    setForm({ mese_riferimento: null, importo_previsto: 0, sede: null, note: '' });
  };

  const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const formatMese = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    return d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconCoin size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Commissioni</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuovo Mese
        </Button>
      </Group>

      <Card shadow="sm" padding={isMobile ? 'sm' : 'lg'} radius="md" withBorder>
        <Table.ScrollContainer minWidth={isMobile ? 0 : 600}>
          <Table striped highlightOnHover style={isMobile ? { fontSize: '0.8rem' } : undefined}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Mese</Table.Th>
                {!isMobile && <Table.Th>Sede</Table.Th>}
                <Table.Th ta="right">Importo</Table.Th>
                {!isMobile && <Table.Th>Note</Table.Th>}
                <Table.Th>Azioni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {commissioni.map(c => (
                <Table.Tr key={c.id} onClick={() => isMobile && handleEdit(c)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                  <Table.Td>
                    <Text size={isMobile ? 'xs' : 'sm'} fw={600} tt="capitalize">{formatMese(c.mese_riferimento)}</Text>
                    {isMobile && c.sede && (
                      <Badge variant="light" color={c.sede === 'FCO' ? 'blue' : 'orange'} size="xs" mt={2}>
                        {c.sede}
                      </Badge>
                    )}
                  </Table.Td>
                  {!isMobile && (
                    <Table.Td>
                      {c.sede ? (
                        <Badge variant="light" color={c.sede === 'FCO' ? 'blue' : 'orange'} size="sm">{c.sede}</Badge>
                      ) : <Text c="dimmed" size="sm">-</Text>}
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    <Text size={isMobile ? 'xs' : 'sm'} fw={600}>{formatCurrency(c.importo_previsto)}</Text>
                  </Table.Td>
                  {!isMobile && (
                    <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{c.note || '-'}</Text></Table.Td>
                  )}
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleEdit(c); }}>
                        <IconPencil size={isMobile ? 14 : 16} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(c); }}>
                        <IconTrash size={isMobile ? 14 : 16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {commissioni.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={isMobile ? 3 : 5}>
                    <Text ta="center" c="dimmed" py="xl">Nessuna commissione registrata</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal opened={showModal} onClose={() => setShowModal(false)}
        title={editingCommissione ? 'Modifica Commissione' : 'Nuova Commissione Mensile'} size="md">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <MonthPickerInput label="Mese di Riferimento" required locale="it" valueFormat="MMMM YYYY"
              value={form.mese_riferimento} onChange={(val) => setForm({...form, mese_riferimento: val})} />
            <NumberInput label="Importo" required decimalScale={2} prefix="€ "
              thousandSeparator="." decimalSeparator=","
              value={form.importo_previsto} onChange={(val) => setForm({...form, importo_previsto: val})} />
            <Select label="Sede" placeholder="Seleziona sede" clearable
              data={[{ value: 'FCO', label: 'FCO - Fiumicino' }, { value: 'CIA', label: 'CIA - Ciampino' }]}
              value={form.sede} onChange={(val) => setForm({...form, sede: val})} />
            <Textarea label="Note" rows={2} value={form.note}
              onChange={(e) => setForm({...form, note: e.target.value})} />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setShowModal(false)}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

export default CommissioniAllRent;
